/**
 * <product-gallery> — scroll-snap slider with arrow nav (desktop) +
 * thumb slider that mirrors the active slide.
 * <quantity-input> — increments/decrements the quantity number input.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('.product-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    if (!this.track || this.slides.length <= 1) return;

    this.activeIndex = 0;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.index);
        this.goTo(index);
      });
    });

    this.prevBtn?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.activeIndex + 1));

    this.thumbsTrack = this.querySelector('.product-gallery__thumbs');
    this.bindThumbDrag();
    this.bindThumbArrows();

    this.observeActiveSlide();
    this.updateArrows();
    this.bindLightbox();
  }

  bindThumbArrows() {
    if (!this.thumbsTrack) return;
    const prev = this.querySelector('[data-thumbs-prev]');
    const next = this.querySelector('[data-thumbs-next]');
    if (!prev && !next) return;

    const scrollByOne = (dir) => {
      const firstItem = this.thumbsTrack.querySelector('.product-gallery__thumb-item');
      const step = firstItem ? firstItem.getBoundingClientRect().width + 12 : 100;
      this.thumbsTrack.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollByOne(-1));
    next?.addEventListener('click', () => scrollByOne(1));

    const updateState = () => {
      const max = this.thumbsTrack.scrollWidth - this.thumbsTrack.clientWidth;
      if (prev) prev.disabled = this.thumbsTrack.scrollLeft <= 1;
      if (next) next.disabled = this.thumbsTrack.scrollLeft >= max - 1;
    };
    this.thumbsTrack.addEventListener('scroll', updateState, { passive: true });
    requestAnimationFrame(updateState);
  }

  bindLightbox() {
    this.lightbox = this.querySelector('[data-lightbox]');
    this.lightboxTrack = this.querySelector('[data-lightbox-track]');
    this.lightboxSlides = Array.from(
      this.querySelectorAll('.product-gallery__lightbox-slide'),
    );
    if (!this.lightbox) return;

    this.querySelectorAll('.product-gallery__slide-image').forEach((img) => {
      img.addEventListener('click', () => this.openLightbox());
    });

    this.querySelectorAll('[data-lightbox-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeLightbox());
    });

    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => {
      this.lightboxGoTo(this.lightboxIndex() - 1);
    });
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => {
      this.lightboxGoTo(this.lightboxIndex() + 1);
    });

    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.closeLightbox();
    });

    document.addEventListener('keydown', (e) => {
      if (!this.lightbox.open) return;
      if (e.key === 'ArrowLeft') this.lightboxGoTo(this.lightboxIndex() - 1);
      if (e.key === 'ArrowRight') this.lightboxGoTo(this.lightboxIndex() + 1);
    });
  }

  openLightbox() {
    if (!this.lightbox) return;
    this.lightbox.showModal();
    requestAnimationFrame(() => this.lightboxGoTo(this.activeIndex || 0, false));
  }

  closeLightbox() {
    this.lightbox?.close();
  }

  lightboxIndex() {
    if (!this.lightboxTrack) return 0;
    const slideWidth = this.lightboxTrack.clientWidth;
    return Math.round(this.lightboxTrack.scrollLeft / slideWidth);
  }

  lightboxGoTo(index, smooth = true) {
    if (!this.lightboxTrack || this.lightboxSlides.length === 0) return;
    const max = this.lightboxSlides.length - 1;
    const clamped = Math.max(0, Math.min(max, index));
    this.lightboxTrack.scrollTo({
      left: clamped * this.lightboxTrack.clientWidth,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  bindThumbDrag() {
    if (!this.thumbsTrack) return;

    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const onDown = (e) => {
      const point = e.touches ? e.touches[0] : e;
      isDown = true;
      moved = false;
      startX = point.clientX;
      startScroll = this.thumbsTrack.scrollLeft;
    };

    const onMove = (e) => {
      if (!isDown) return;
      const point = e.touches ? e.touches[0] : e;
      const delta = point.clientX - startX;
      if (Math.abs(delta) > 8 && !moved) {
        moved = true;
        this.thumbsTrack.classList.add('is-dragging');
      }
      if (moved) {
        this.thumbsTrack.scrollLeft = startScroll - delta;
        if (e.cancelable) e.preventDefault();
      }
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      this.thumbsTrack.classList.remove('is-dragging');
    };

    this.thumbsTrack.addEventListener('mousedown', onDown);
    this.thumbsTrack.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    this.thumbsTrack.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  goTo(index) {
    const max = this.slides.length - 1;
    const clamped = Math.max(0, Math.min(max, index));
    const slide = this.slides[clamped];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  observeActiveSlide() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 0.6) return;
          const index = Number(entry.target.dataset.slideIndex);
          this.setActive(index);
        });
      },
      { root: this.track, threshold: 0.6 },
    );

    this.slides.forEach((slide) => observer.observe(slide));
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.thumbs.forEach((thumb) => {
      const isActive = Number(thumb.dataset.index) === index;
      thumb.classList.toggle('product-gallery__thumb--active', isActive);
      if (isActive) {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });

    this.dots.forEach((dot) => {
      const isActive = Number(dot.dataset.index) === index;
      dot.classList.toggle('product-gallery__dot--active', isActive);
    });

    this.updateArrows();
  }

  updateArrows() {
    if (this.prevBtn) this.prevBtn.disabled = this.activeIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.activeIndex === this.slides.length - 1;
  }
}

customElements.define('product-gallery', ProductGallery);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-qty-input]');
    this.decreaseBtn = this.querySelector('[data-qty-decrease]');
    this.increaseBtn = this.querySelector('[data-qty-increase]');
    if (!this.input) return;

    this.decreaseBtn?.addEventListener('click', () => this.adjust(-1));
    this.increaseBtn?.addEventListener('click', () => this.adjust(1));
  }

  adjust(delta) {
    const min = parseInt(this.input.min || '1', 10);
    const current = parseInt(this.input.value || '1', 10);
    const next = Math.max(min, current + delta);
    this.input.value = String(next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('quantity-input', QuantityInput);

/**
 * <sticky-buy> — slides in from the bottom when the buybox CTA is out of
 * the viewport. Click submits the original product form (intercepted by
 * the cart-drawer for AJAX add).
 */
class StickyBuy extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;

    this.formId = this.dataset.form;
    this.trigger = document.querySelector(this.dataset.trigger);
    this.cta = this.querySelector('[data-sticky-cta]');
    if (!this.trigger || !this.cta || !this.formId) return;

    this.removeAttribute('hidden');

    const observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = entry.boundingClientRect.top < 0;
        if (!entry.isIntersecting && scrolledPast) {
          this.setAttribute('active', '');
        } else {
          this.removeAttribute('active');
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' },
    );
    observer.observe(this.trigger);

    this.cta.addEventListener('click', () => {
      const form = document.getElementById(this.formId);
      if (!form) return;
      form.requestSubmit();
    });
  }
}

customElements.define('sticky-buy', StickyBuy);
